import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
    Box,
    Grid,
    useTheme,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Button from '../../components/button';
import { InputField, InputFieldComponent } from '../../components/formItem';
import CommonCard from '../../components/card/CommonCard';
import { userAPI, membershipAPI, organizationAPI } from '../../actions/imsServer';
import Popup from '../../components/popup';
import { isSupervisor, isAdmin } from '../../utils/base';
import { isEmail, isValidPassword } from '../../utils/password-validation';

const GeneralEdit = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    // 전달받은 관리자 데이터 (수정 모드)
    const userData = location.state?.user;
    const isEditMode = !!userData;

    // 폼 데이터 상태
    const [formData, setFormData] = useState({
        loginId: '', // ID (메일형식)
        password: '', // 비밀번호
        confirmPassword: '', // 비밀번호 재확인
        name: '', // 이름
        phone: '', // 전화번호
    });

    // 계정 중복 확인 상태
    const [isCheckingLoginId, setIsCheckingLoginId] = useState(false);
    const [loginIdExists, setLoginIdExists] = useState(false);

    // 수정 확인 팝업 상태
    const [updatePopupOpen, setUpdatePopupOpen] = useState(false);

    // 삭제 확인 팝업 상태
    const [deletePopupOpen, setDeletePopupOpen] = useState(false);

    // 일반 알림 팝업 상태
    const [alertPopupOpen, setAlertPopupOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSubMessage, setAlertSubMessage] = useState('');

    const [meInfo, setMeInfo] = useState(JSON.parse(localStorage.getItem('meInfo')));
    const [organizationId, setOrganizationId] = useState(0);

    useEffect(() => {
        setMeInfo(JSON.parse(localStorage.getItem('meInfo')));
        const fetchOrganizationId = async () => {
            try {
                // grade 50, 60 모두 membership에서 가져오기
                const membershipsResponse = await membershipAPI.getMemberships({ userId: meInfo.id, type: 0, state: 0, expand: 'organization' });
                const memberships = membershipsResponse?.data?.memberships || [];
                if (memberships.length > 0) {
                    setOrganizationId(memberships[0].organizationId);
                }
            } catch (err) {
                console.error('Failed to fetch organization ID:', err);
            }
        }
        fetchOrganizationId();
    }, []);

    // 수정 모드일 때 기존 데이터 로드
    useEffect(() => {
        if (isEditMode && userData) {
            setFormData({
                loginId: userData.loginId || '',
                password: '',
                confirmPassword: '',
                name: userData.name || '',
                phone: userData.mobile || userData.phone || '',
            });
        }
    }, [isEditMode, userData]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // 계정 중복 확인 함수
    const checkLoginIdExists = async (loginId) => {
        if (!loginId || !validateEmail(loginId)) {
            setLoginIdExists(false);
            return;
        }

        // 수정 모드에서 기존 loginId와 동일한 경우 중복 확인 생략
        if (isEditMode && userData && userData.loginId === loginId) {
            setLoginIdExists(false);
            return;
        }

        try {
            setIsCheckingLoginId(true);
            const response = await userAPI.checkUserExists({ loginId: loginId });
            setLoginIdExists(response.data.exists || false);
        } catch (error) {
            console.error('계정 중복 확인 실패:', error);
            setLoginIdExists(false);
        } finally {
            setIsCheckingLoginId(false);
        }
    };

    // 계정 입력 변경 핸들러 (디바운스 적용)
    const handleLoginIdChange = (value) => {
        handleInputChange('loginId', value);

        // 디바운스를 위해 기존 타이머 클리어
        if (window.loginIdCheckTimer) {
            clearTimeout(window.loginIdCheckTimer);
        }

        // 500ms 후에 중복 확인 실행
        window.loginIdCheckTimer = setTimeout(() => {
            checkLoginIdExists(value);
        }, 500);
    };

    // 유효성 검사 함수들
    const validateEmail = (email) => email && isEmail(email);
    const validatePassword = (password) => password && isValidPassword(password);

    const validateName = (name) => {
        return name && name.trim().length > 0;
    };

    const validatePhone = (phone) => {
        return phone && phone.trim().length > 0;
    };

    // 전체 폼 유효성 검사
    const isFormValid = () => {
        const baseValidation = validateEmail(formData.loginId) &&
            !loginIdExists &&
            validateName(formData.name) &&
            validatePhone(formData.phone);

        if (isEditMode) {
            // 수정 모드: 비밀번호가 입력된 경우에만 검증
            const passwordValidation = formData.password
                ? (validatePassword(formData.password) && formData.password === formData.confirmPassword)
                : true; // 비밀번호가 비어있으면 검증 통과
            return baseValidation && passwordValidation;
        } else {
            // 생성 모드: 비밀번호 필수
            return baseValidation &&
                validatePassword(formData.password) &&
                formData.password === formData.confirmPassword;
        }
    };

    const handleSubmit = () => {
        if (!isFormValid()) {
            showAlert(t('please_check_your_input'));
            return;
        }
        setUpdatePopupOpen(true);
    };

    // 저장 확인 핸들러
    const handleConfirmUpdate = async () => {
        try {
            setUpdatePopupOpen(false);

            if (isEditMode && userData) {
                // 수정 모드
                // loginId가 변경된 경우
                if (userData.loginId !== formData.loginId) {
                    // 새 계정 생성
                    const newUserData = {
                        loginId: formData.loginId,
                        loginPwd: formData.password,
                        grade: isSupervisor(meInfo?.grade) ? 80 : 50,
                        state: 0,
                        name: formData.name,
                        mobile: formData.phone,
                        email: formData.loginId, // 메일형식이므로 loginId와 동일
                    };

                    await userAPI.registerUser(newUserData);
                } else {
                    // loginId가 동일한 경우 - 기존 사용자 정보 업데이트
                    const isUserInfoChanged =
                        (userData.name !== formData.name) ||
                        (userData.mobile !== formData.phone);

                    if (isUserInfoChanged) {
                        await userAPI.updateUser({
                            id: userData.id,
                            name: formData.name,
                            mobile: formData.phone,
                            email: formData.loginId,
                        });
                    }

                    // 비밀번호가 입력된 경우에만 비밀번호도 업데이트
                    if (formData.password && formData.password.trim() !== '') {
                        await userAPI.resetPassword({ loginId: userData.loginId, newPassword: formData.password });
                    }
                }
            } else {
                // 생성 모드
                const newUserData = {
                    loginId: formData.loginId,
                    loginPwd: formData.password,
                    grade: isSupervisor(meInfo?.grade) ? 80 : 50,
                    state: 0,
                    name: formData.name,
                    mobile: formData.phone,
                    email: formData.loginId, // 메일형식이므로 loginId와 동일
                };

                const userResponse = await userAPI.registerUser(newUserData);

                if (isAdmin(meInfo?.grade)) {
                    const userId = userResponse.data.user.id;

                    await membershipAPI.registerMembership({
                        userId: userId,
                        organizationId: organizationId,
                        type: 0,
                        state: 0,
                    });
                }
            }

            showAlert(t('general_admin_saved_successfully'));
            navigate('/generals/list');
        } catch (error) {
            console.error('일반관리자 저장 실패:', error);
            showAlert(t('general_admin_save_failed'));
        }
    };

    // 저장 취소 핸들러
    const handleCancelUpdate = () => {
        setUpdatePopupOpen(false);
    };

    const handleCancel = () => {
        navigate('/generals/list');
    };

    // 삭제 버튼 클릭 핸들러
    const handleDelete = () => {
        setDeletePopupOpen(true);
    };

    // 삭제 확인 핸들러
    const handleConfirmDelete = async () => {
        try {
            setDeletePopupOpen(false);

            if (isEditMode && userData) {

                if (organizationId !== 0) {
                    await membershipAPI.unregisterMembership({
                        userId: userData.id,
                        organizationId: organizationId,
                    });
                }

                // 사용자 삭제
                await userAPI.unregisterUser({
                    userId: userData.id,
                    type: 1
                });

                showAlert(t('general_admin_deleted_successfully'));
                navigate('/generals/list');
            }
        } catch (error) {
            console.error('일반관리자 삭제 실패:', error);
            showAlert(t('general_admin_delete_failed'));
        }
    };

    // 삭제 취소 핸들러
    const handleCancelDelete = () => {
        setDeletePopupOpen(false);
    };

    // 알림 팝업 표시 함수
    const showAlert = (message, subMessage = '') => {
        setAlertMessage(message);
        setAlertSubMessage(subMessage);
        setAlertPopupOpen(true);
    };

    // 알림 팝업 닫기 함수
    const handleCloseAlert = () => {
        setAlertPopupOpen(false);
    };

    return (
        <ContentArea padding='20px 10px 10px 10px'>
            {/* 폼 영역 */}
            <Box sx={{ px: 3, pb: 4, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Grid container spacing={4} sx={{ flex: 1 }}>
                    {/* 왼쪽 컬럼 */}
                    <Grid item xs={12} md={6} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CommonCard sx={{ alignItems: 'stretch', gap: 3 }}>
                            <InputFieldComponent title={`${t('login_id')} *`}>
                                <InputField
                                    placeholder={t('email_format')}
                                    value={formData.loginId}
                                    onChange={(e) => handleLoginIdChange(e.target.value)}
                                    disabled={isEditMode}
                                    error={formData.loginId && (!validateEmail(formData.loginId) || loginIdExists)}
                                    helperText={
                                        (formData.loginId && !validateEmail(formData.loginId)
                                            ? t('please_enter_a_valid_email_address')
                                            : formData.loginId && loginIdExists
                                                ? t('account_in_use')
                                                : formData.loginId && isCheckingLoginId
                                                    ? t('checking_account_availability')
                                                    : formData.loginId && !loginIdExists && validateEmail(formData.loginId)
                                                        ? t('account_is_available')
                                                        : ' ')
                                    }
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={`${t('password')} *`}>
                                <InputField
                                    type="password"
                                    showVisibilityIcon={true}
                                    placeholder={t('password_requirement_placeholder')}
                                    value={formData.password}
                                    onChange={(e) => handleInputChange('password', e.target.value)}
                                    error={
                                        (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) ||
                                        (formData.password && !validatePassword(formData.password))
                                    }
                                    helperText={
                                        (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword)
                                            ? t('password_mismatch')
                                            : (formData.password && !validatePassword(formData.password)
                                                ? t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters')
                                                : isEditMode && !formData.password
                                                    ? t('leave_blank_if_you_do_not_want_to_change_password')
                                                    : ' ')
                                    }
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={`${t('confirm_password')} *`}>
                                <InputField
                                    type="password"
                                    showVisibilityIcon={true}
                                    placeholder={t('password_requirement_placeholder')}
                                    value={formData.confirmPassword}
                                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                    error={
                                        (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) ||
                                        (isEditMode && formData.confirmPassword && !formData.password)
                                    }
                                    helperText={
                                        (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword)
                                            ? t('password_mismatch')
                                            : (isEditMode && formData.confirmPassword && !formData.password)
                                                ? t('please_enter_password_first')
                                                : ' '
                                    }
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={`${t('name')} *`}>
                                <InputField
                                    placeholder={t('please_enter')}
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    error={formData.name && !validateName(formData.name)}
                                    helperText={formData.name && !validateName(formData.name) ? t('please_enter_your_name') : ' '}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={`${t('phone_number')} *`}>
                                <InputField
                                    placeholder={t('please_enter')}
                                    value={formData.phone}
                                    onChange={(e) => handleInputChange('phone', e.target.value)}
                                    error={formData.phone && !validatePhone(formData.phone)}
                                    helperText={formData.phone && !validatePhone(formData.phone) ? t('please_enter_phone_number_numbers_only') : ' '}
                                />
                            </InputFieldComponent>
                        </CommonCard>
                    </Grid>
                </Grid>

                {/* 하단 버튼 영역 */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    px: 2.5,
                    pb: 3,
                    alignSelf: 'stretch',
                    marginTop: 'auto'
                }}>
                    {isEditMode && (
                        <Button
                            variant="gray"
                            onClick={handleDelete}
                            sx={{ minWidth: '120px' }}
                        >
                            {t('delete')}
                        </Button>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, marginLeft: 'auto' }}>
                        <Button
                            variant="gray"
                            onClick={handleCancel}
                            sx={{ minWidth: '120px' }}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={!isFormValid()}
                            sx={{ minWidth: '120px' }}
                        >
                            {t('save_button')}
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* 저장 확인 팝업 */}
            <Popup
                open={updatePopupOpen}
                message={isEditMode ? t('general_admin_confirm_edit') : t('general_admin_confirm_register')}
                subMessage={isEditMode ? t('general_admin_updated_will_save') : t('general_admin_registered_will_activate')}
                onCancel={handleCancelUpdate}
                onConfirm={handleConfirmUpdate}
            />

            {/* 삭제 확인 팝업 */}
            <Popup
                open={deletePopupOpen}
                message={t('general_admin_confirm_delete')}
                subMessage={t('general_admin_deleted_cannot_recover')}
                onCancel={handleCancelDelete}
                onConfirm={handleConfirmDelete}
            />

            {/* 일반 알림 팝업 */}
            <Popup
                open={alertPopupOpen}
                message={alertMessage}
                subMessage={alertSubMessage}
                onConfirm={handleCloseAlert}
            />
        </ContentArea>
    );
};

export default GeneralEdit;
